import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

interface Post {
  id: number;
  description: string;
  createdAt: string;
  author: {
    id: number;
    username: string;
    name: string;
  };
  images: any[];
  _count: {
    likes: number;
    comments: number;
  };
}

const PostList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [posts, setPosts] = useState<Post[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [username, setUsername] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<number | null>(null);

  useEffect(() => {
    fetchPosts();
  }, [page, rowsPerPage]);

  const fetchPosts = async () => {
    try {
      setIsLoading(true);
      setError('');

      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (search) params.search = search;
      if (username) params.username = username;

      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/posts`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setPosts(response.data.items);
      setTotal(response.data.meta.total);
    } catch (err: any) {
      setError(err.response?.data?.message || '게시물 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchPosts();
  };

  const handleDelete = async () => {
    if (!postToDelete) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/admin/posts/${postToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setDeleteDialogOpen(false);
      setPostToDelete(null);
      fetchPosts();
    } catch (err: any) {
      setError(err.response?.data?.message || '게시물 삭제에 실패했습니다.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">게시물 관리</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
          <TextField
            label="검색 (게시물 내용)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
            sx={{ flexGrow: 1 }}
          />
          <TextField
            label="작성자 (username)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
            sx={{ width: { xs: '100%', sm: 200 } }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} fullWidth={isMobile}>
              검색
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchPosts} fullWidth={isMobile}>
              새로고침
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isMobile ? (
        <Stack spacing={1.5}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : posts.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">검색 결과가 없습니다</Typography>
            </Paper>
          ) : (
            posts.map((post) => {
              const firstImage = post.images && post.images.length > 0 ? post.images[0] : null;
              const imageUrl = firstImage ? `${API_BASE_URL}/uploads/posts/${firstImage.basename}` : null;
              return (
                <Card key={post.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/posts/${post.id}`)}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1.5}>
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                          onError={(e: any) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Box sx={{ width: 72, height: 72, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Typography variant="caption" color="text.secondary">없음</Typography>
                        </Box>
                      )}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary">
                          #{post.id} · @{post.author.username}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.3, mt: 0.5,
                        }}>
                          {post.description}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                          {post.images.length > 1 && (
                            <Chip
                              icon={<ImageIcon />}
                              label={`+${post.images.length - 1}`}
                              size="small"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            ♥ {post._count.likes} · 💬 {post._count.comments}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(post.createdAt)}
                          </Typography>
                        </Stack>
                      </Box>
                      <Stack direction="column" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setPostToDelete(post.id);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage=""
          />
        </Stack>
      ) : (
      <TableContainer component={Paper}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>이미지</TableCell>
                <TableCell>작성자</TableCell>
                <TableCell>게시물 내용</TableCell>
                <TableCell align="center">좋아요</TableCell>
                <TableCell align="center">댓글</TableCell>
                <TableCell>작성일</TableCell>
                <TableCell align="center">작업</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      검색 결과가 없습니다
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                posts.map((post) => {
                const firstImage = post.images && post.images.length > 0 ? post.images[0] : null;
                const imageUrl = firstImage
                  ? `${API_BASE_URL}/uploads/posts/${firstImage.basename}`
                  : null;

                return (
                  <TableRow
                    key={post.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/posts/${post.id}`)}
                  >
                    <TableCell>{post.id}</TableCell>
                    <TableCell>
                      {imageUrl ? (
                        <Box
                          component="img"
                          src={imageUrl}
                          alt="게시물 이미지"
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            이미지 없음
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{post.author.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{post.author.username}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {post.description}
                      </Typography>
                      {post.images.length > 1 && (
                        <Chip
                          icon={<ImageIcon />}
                          label={`+${post.images.length - 1}`}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">{post._count.likes}</TableCell>
                    <TableCell align="center">{post._count.comments}</TableCell>
                    <TableCell>{formatDate(post.createdAt)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPostToDelete(post.id);
                          setDeleteDialogOpen(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="페이지당 개수:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 총 ${count}개`}
        />
      </TableContainer>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>게시물 삭제 확인</DialogTitle>
        <DialogContent>
          이 게시물을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PostList;
